import { useState, useCallback } from "react";
import { TicketsService } from "@/services";
import toast from "react-hot-toast";

interface FilePreview {
  url: string;
  nome: string;
}

export const useFileHandlers = () => {
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [selectedImage, setSelectedImage] = useState<FilePreview | null>(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreview, setPdfPreview] = useState<FilePreview | null>(null);
  const [pdfNumPages, setPdfNumPages] = useState<number>(0);
  const [pdfPageNumber, setPdfPageNumber] = useState<number>(1);

  const downloadFile = useCallback(async (anexo: any) => {
    try {
      // Tentar usar a API real para download
      if (anexo.id && typeof anexo.id === "number") {
        const response = await TicketsService.downloadTicketAnexo(anexo.id);
        const blob = new Blob([response], { type: "application/octet-stream" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = anexo.nome || "anexo";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return;
      }
    } catch (apiError) {
    }

    // Fallback para download direto via URL
    try {
      const url = `${
        import.meta.env.VITE_API_URL || "http://localhost:8000"
      }/${anexo.caminho}`;

      const link = document.createElement("a");
      link.href = url;
      link.download = anexo.nome || "anexo";
      link.target = "_blank";
      link.rel = "noopener noreferrer";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Erro ao fazer download do anexo:", error);
      const url = `${
        import.meta.env.VITE_API_URL || "http://localhost:8000"
      }/${anexo.caminho}`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, []);

  const openPdfPreview = useCallback(async (anexo: any) => {
    try {
      if (anexo.id && typeof anexo.id === "number") {
        const blob = await TicketsService.downloadTicketAnexo(anexo.id);
        const url = window.URL.createObjectURL(
          new Blob([blob], { type: "application/pdf" })
        );
        setPdfPreview({ url, nome: anexo.nome || "documento.pdf" });
        setShowPdfPreview(true);
        setPdfPageNumber(1);
      } else {
        const url = `${
          import.meta.env.VITE_API_URL || "http://localhost:8000"
        }/${anexo.caminho}`;
        setPdfPreview({ url, nome: anexo.nome || "documento.pdf" });
        setShowPdfPreview(true);
        setPdfPageNumber(1);
      }
    } catch (error) {
      console.error("Erro ao abrir visualização do PDF:", error);
      try {
        const baseUrl = import.meta.env.VITE_API_URL || "http://localhost:8000";
        const token = localStorage.getItem("token");
        const viewUrl = `${baseUrl}/api/tickets/anexos/${anexo.id}/view${
          token ? `?token=${encodeURIComponent(token)}` : ""
        }`;
        setPdfPreview({ url: viewUrl, nome: anexo.nome || "documento.pdf" });
        setShowPdfPreview(true);
        setPdfPageNumber(1);
        return;
      } catch (_) {
        // ignore
      }
      try {
        const directUrl = `${
          import.meta.env.VITE_API_URL || "http://localhost:8000"
        }/${anexo.caminho}`;
        setPdfPreview({ url: directUrl, nome: anexo.nome || "documento.pdf" });
        setShowPdfPreview(true);
        setPdfPageNumber(1);
      } catch (_) {
        toast.error("Não foi possível visualizar o PDF");
      }
    }
  }, []);

  const handleViewFile = useCallback(
    (anexo: any) => {
      const fileName = anexo.nome || "";
      const fileExtension = fileName.toLowerCase().split(".").pop();

      // Se for imagem, abrir modal
      if (["jpg", "jpeg", "png", "gif", "webp"].includes(fileExtension)) {
        const imageUrl = `${
          import.meta.env.VITE_API_URL || "http://localhost:8000"
        }/${anexo.caminho}`;
        setSelectedImage({ url: imageUrl, nome: fileName });
        setShowImagePreview(true);
      }
      // Se for PDF, abrir visualização dentro do frontend (modal)
      else if (fileExtension === "pdf") {
        openPdfPreview(anexo);
      }
      // Para outros tipos, apenas download
      else {
        downloadFile(anexo);
      }
    },
    [downloadFile, openPdfPreview]
  );

  const canPreviewFile = useCallback((anexo: any) => {
    const fileName = anexo.nome || "";
    const fileExtension = fileName.toLowerCase().split(".").pop();
    return ["jpg", "jpeg", "png", "gif", "webp", "pdf"].includes(fileExtension);
  }, []);

  const closeImagePreview = useCallback(() => {
    setShowImagePreview(false);
    setSelectedImage(null);
  }, []);

  const closePdfPreview = useCallback(() => {
    setShowPdfPreview(false);
    if (pdfPreview?.url?.startsWith("blob:")) {
      try {
        window.URL.revokeObjectURL(pdfPreview.url);
      } catch {}
    }
    setPdfPreview(null);
    setPdfNumPages(0);
    setPdfPageNumber(1);
  }, [pdfPreview]);

  return {
    showImagePreview,
    selectedImage,
    showPdfPreview,
    pdfPreview,
    pdfNumPages,
    pdfPageNumber,
    setPdfNumPages,
    setPdfPageNumber,
    downloadFile,
    handleViewFile,
    canPreviewFile,
    closeImagePreview,
    closePdfPreview,
  };
};

